import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchResource } from '@app/src/api/fetchHooks'
import { useUpdateResource } from '@app/src/api/updateHooks'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import useErrorNotification from '@app/src/hooks/errorNotification'
import type { OrganizationSettings } from '@app/src/wf-constants'
import { NotificationSeverity, ONE_MINUTE_MS } from '@app/src/wf-constants'
import { useIntl } from 'react-intl'
import { useQueryClient } from '@tanstack/react-query'

interface UseOrganizationSettingsProps {
  enabled?: boolean
}

const useOrganizationSettings = ({ enabled = true }: UseOrganizationSettingsProps = {}) => {
  const { formatMessage } = useIntl()
  const { showSnackbar } = useSnackbar()
  const { showErrorNotification } = useErrorNotification()
  const queryClient = useQueryClient()
  const { mutateAsync, isPending: isSaving, isError: updateError } = useUpdateResource<Partial<OrganizationSettings>>()
  const {
    data: settings,
    isLoading,
    isFetching,
    isFetched,
    error: fetchError,
  } = useFetchResource<OrganizationSettings>({
    key: FetchKey.OrganizationSettings,
    endpoint: endpoints.organizationSettings,
    options: { enabled: enabled, staleTime: ONE_MINUTE_MS },
  })
  const updateOrganizationSettings = async (settings: Partial<OrganizationSettings>, customSuccessMessage?: string) => {
    await mutateAsync(
      {
        url: endpoints.organizationSettings,
        body: settings,
      },
      {
        onError: error => {
          showErrorNotification({ requestError: error })
        },
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [FetchKey.OrganizationSettings] })
          showSnackbar({
            message: customSuccessMessage
              ? customSuccessMessage
              : formatMessage({ id: 'general.organizationSettingsSaved' }),
            severity: NotificationSeverity.success,
          })
        },
      },
    )
  }

  return {
    settings,
    updateOrganizationSettings,
    isSaving,
    updateError,
    fetchError,
    isLoading,
    isFetching,
    isFetched,
  }
}

export default useOrganizationSettings
